import { apiRequest, getApiBaseUrl } from "./client";

// One uploaded "object" / scene element for a video. The image bytes are
// served separately at `imageUrl` (a relative path the FE turns absolute
// via `entityImageUrl`). Returned by the list + create endpoints.
export type VideoEntity = {
  id: string;
  videoId: string;
  label: string;
  mimeType: string;
  tSec: number | null;
  createdAt: string;
  imageUrl: string;
};

export const videoEntitiesApi = {
  list: (videoId: string, token: string): Promise<VideoEntity[]> =>
    apiRequest<VideoEntity[]>(`/v1/videos/${videoId}/entities`, { token }),

  // `dataUrl` is the standard data:image/png;base64,… string produced by
  // <input type=file> + FileReader.readAsDataURL. The backend enforces a
  // 2 MB cap and rejects anything other than PNG / JPEG / WebP.
  create: (
    videoId: string,
    body: { label: string; dataUrl: string; tSec?: number | null },
    token: string,
  ): Promise<VideoEntity> =>
    apiRequest<VideoEntity>(`/v1/videos/${videoId}/entities`, {
      method: "POST",
      body,
      token,
    }),

  remove: (entityId: string, token: string): Promise<{ ok: boolean }> =>
    apiRequest<{ ok: boolean }>(`/v1/videos/entities/${entityId}`, {
      method: "DELETE",
      token,
    }),
};

// Fetch the entity image as a Blob using the auth'd Bearer token, return
// an object-URL safe to plug into <img src>. The browser can't attach a
// Bearer header to a bare <img src>, so we proxy through fetch + Blob +
// URL.createObjectURL. Caller is responsible for revoking the URL when
// the consuming component unmounts.
export async function fetchEntityImageObjectUrl(
  entity: VideoEntity,
  token: string,
): Promise<string> {
  const res = await fetch(`${getApiBaseUrl()}${entity.imageUrl}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    throw new Error(`Entity image fetch failed: ${res.status}`);
  }
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}
